import axios from "axios";
import Cookies from "js-cookie";
import { globalStorage } from "../helpers";

// Global request interceptor: attach SSO Bearer token to all outgoing requests
axios.interceptors.request.use(
  (config) => {
    const token = Cookies.get("fin_access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const selectedUnit = Cookies.get("fin_unit_data");
    // Only default to the selected unit when the caller did not explicitly
    // target one (e.g. updating a budget must send that budget's own unit,
    // which may differ from the currently selected unit).
    // "0" is the legacy placeholder for no selected unit. It must not be
    // sent as a unit code; budgeting will resolve a sole assigned SSO unit.
    if (selectedUnit && selectedUnit !== "*" && selectedUnit !== "0" && !config.headers["X-Unit-ID"]) {
      config.headers["X-Unit-ID"] = selectedUnit;
    }
    const organizationId = Cookies.get("fin_organization_id");
    if (organizationId) {
      config.headers["X-Organization-ID"] = organizationId;
    }

    // Budgeting now resolves the tenant from the authenticated session. Keep
    // callers compatible while removing the old base64 organization segment.
    const user = globalStorage.getItem("fin_user_data");
    const organizationCode = user?.organization_code;
    if (organizationCode && typeof config.url === "string") {
      // Keep the original scheme+host so rewritten paths stay on the API
      // origin; an un-prefixed path would resolve against the dev server.
      const originMatch = config.url.match(/^(https?:\/\/[^/]+)/);
      const origin = originMatch ? originMatch[1] : "";
      const originRe = origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const encodedOrg = window.btoa(organizationCode);
      const masterMatch = config.url.match(new RegExp(`/(activities|activity-categories|budget-posts)/${encodedOrg}(?:/(.*))?$`));
      if (masterMatch) {
        const [, resource, suffix = ""] = masterMatch;
        const staticSuffixes = ["", "options", "template", "import", "reorder"];
        config.url = origin + (staticSuffixes.includes(suffix)
          ? `/${resource}${suffix ? `/${suffix}` : ""}`
          : `/${resource}/item/${suffix}`);
      }
      const capPrefix = `/budget-caps/${encodedOrg}/`;
      if (config.url === `${origin}/budget-caps/${encodedOrg}`) {
        config.url = `${origin}/budget-caps`;
      }
      const capPrefixFull = `${origin}${capPrefix}`;
      if (config.url.startsWith(capPrefixFull)) {
        const suffix = config.url.slice(capPrefixFull.length);
        config.url = `${origin}/budget-caps/${config.method?.toLowerCase() === "get"
          ? `budget/${suffix}`
          : `item/${suffix}`}`;
      }
      config.url = config.url
        .replace(`/budget-details/${encodedOrg}/`, "/budget-details/item/")
        .replace(`/budget-details-posts/${encodedOrg}/`, "/budget-details-posts/item/");

      // Strip the org hash for resources that now have clean routes.
      // Named segments keep their path; ID segments move under /item/.
      const rewriteHashed = (base, staticSegments) => {
        const match = config.url.match(new RegExp(`^${originRe}${base}/${encodedOrg}(?:/(.*))?$`));
        if (!match) return;
        const suffix = match[1] || "";
        if (suffix === "") {
          config.url = `${origin}${base}`;
          return;
        }
        const isStatic = staticSegments.some(
          (segment) => suffix === segment || suffix.startsWith(`${segment}/`)
        );
        config.url = `${origin}${base}/${isStatic ? suffix : `item/${suffix}`}`;
      };
      rewriteHashed("/realizations", ["options", "reports", "settings"]);
      rewriteHashed("/fund-requests", ["budget-options", "settings"]);
      rewriteHashed("/workflows", ["entity-types", "entity"]);

      // Workflow engine: /workflow/{org}/{entityType}/{entityId}[/suffix]
      const workflowMatch = config.url.match(
        new RegExp(`^${originRe}/workflow/${encodedOrg}/([^/]+)/([^/]+)(?:/(.*))?$`)
      );
      if (workflowMatch) {
        const [, entityType, entityId, suffix = ""] = workflowMatch;
        config.url = `${origin}/workflow/entity/${entityType}/${entityId}${suffix ? `/${suffix}` : ""}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global response interceptor: handle 401 (token expired) by redirecting to login
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Tenant DB still being provisioned — retry once after a short delay
    if (
      error.response?.status === 503 &&
      error.response?.data?.error === "database_preparing" &&
      !error.config.__dbPrepareRetried
    ) {
      error.config.__dbPrepareRetried = true;
      return new Promise((resolve) =>
        setTimeout(() => resolve(axios.request(error.config)), 1500)
      );
    }
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      Cookies.remove("fin_access_token");
      Cookies.remove("fin_refresh_token");
      Cookies.remove("fin_unit_data");
      Cookies.remove("fin_unit_name");
      Cookies.remove("fin_organization_id");
      globalStorage.removeItem("fin_user_data");
      globalStorage.removeItem("fin_menu_data");
      globalStorage.removeItem("fin_units");

      // Only redirect if not already on login page
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
